/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { ChromePicker } from 'react-color';
import { LocaleContext } from '@/contexts/locale-context';
import { ThemeContext } from '@/contexts/theme-context';
import { TelemetryContext } from '@/contexts/telemetry-context';
import ClickAway from '@/components/click-away';
import SimpleButton from '@/components/button-simple';
import { AuthContext } from '@/contexts/auth-context';

/**
 * Color picker component
 * @param {string} inputSelectorId an ID to be passed to the component
 * @param {function} onChange a callback being called when selecting a color
 * @param {string} color String representation of color
 * @param {boolean} enabled is the element enabled
 * @return {JSX.Element}
 */
export default function ColorPicker({
  onChange,
  color,
  inputSelectorId,
  enabled,
  telemetryEvent,
  permissionId,
}) {
  const { translate } = useContext(LocaleContext);
  const { primaryColor } = useContext(ThemeContext);
  const telemetryContext = useContext(TelemetryContext);
  const { hasPermission } = useContext(AuthContext);
  const isDisabledByRbac = permissionId ? !hasPermission(permissionId) : false;

  const isEnabled = enabled && !isDisabledByRbac;
  const [currentColor, setCurrentColor] = useState(color);
  const [palleteVisible, setPalleteVisible] = useState(false);

  const openPallete = () => { if (isEnabled) setPalleteVisible(true); };
  return (
    <ClickAway shouldWeListen={palleteVisible} onClickAway={() => setPalleteVisible(false)}>
      <div id={`${inputSelectorId}-wrapper`} key={inputSelectorId} className={`color-picker-wrapper${isEnabled ? '' : ' disabled'}`}>
        <span
          className={`color-picker-current-color${isEnabled ? '' : ' disabled'}`}
          role="button"
          tabIndex={0}
          onClick={openPallete}
          onKeyDown={openPallete}
        >
          {!color ? primaryColor : color}
        </span>
        <div
          className={`color-picker-circle${isEnabled ? '' : ' disabled'}`}
          role="button"
          tabIndex={0}
          aria-label="Open color pallete"
          onClick={openPallete}
          onKeyDown={openPallete}
        />

        {palleteVisible && (
        <div className="color-pallete-wrapper">
          <ChromePicker
            className="branding-color-picker"
            color={currentColor}
            disableAlpha
            onChange={(value) => setCurrentColor(value?.hex)}
          />
          <div className="confirm-button">
            <SimpleButton
              inputSelectorId="color-selection-button"
              onClick={() => {
                onChange(currentColor);
                setPalleteVisible(false);
                if (telemetryEvent) {
                  telemetryContext?.sendEvent(telemetryEvent, { type: 'colour_selected' });
                }
              }}
            >
              {translate('branding.colorPickerConfirmButton')}
            </SimpleButton>
          </div>
        </div>
        )}

        <style jsx>
          {`
          .color-picker-wrapper {
            text-align: center;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-radius: 20px;
            border: 1px solid var(--color-medium-grey);
            max-width: 170px;
            position: relative;
            margin-left: auto;
          }

          .color-picker-wrapper.disabled {
            border: 1px solid var(--color-disabled-grey);
          }

          .color-picker-circle {
            cursor: pointer;
            width: 40px;
            height: 40px;
            border-radius: 100%;
            background-color: ${!color ? primaryColor : color};
            opacity: ${isDisabledByRbac ? 0.3 : 1};
          }

          .color-picker-circle.disabled {
            cursor: default;
          }

          .color-picker-current-color {
            cursor: pointer;
            margin: 0 auto;
            padding: 0 20px;
          }

          .color-picker-current-color.disabled {
            color: var(--color-disabled-grey);
            cursor: default;
          }

          .color-pallete-wrapper {
            position: absolute;
            top: 0;
            right: 0;
            z-index: var(--zindex-dropdown);
          }

          .confirm-button {
            position: absolute;
            bottom: 10px;
            width: 100%;
          }
        `}
        </style>
      </div>
    </ClickAway>
  );
}

ColorPicker.defaultProps = {
  color: '#000',
  inputSelectorId: null,
  enabled: true,
  telemetryEvent: null,
};

ColorPicker.propTypes = {
  inputSelectorId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  color: PropTypes.string,
  enabled: PropTypes.bool,
  telemetryEvent: PropTypes.string,
};
